/**
 * Reevaluate engine — H-215.
 *
 * The anonymous viewer of a shared trip provides a fresh context (their
 * location / transport / time-available) and we re-rank + re-hook the
 * trip's saved places against that new context. No Google calls — travel
 * times come from haversine × roadFactor (option b in PRD §3, confirmed
 * by Ian session 11).
 *
 * One LLM call per Reevaluate (low-mode equivalent). The LLM gets the
 * places' rating, types, summary-equivalent, opening hours, and computed
 * travel mins — and returns idx + new hook + matchingVibes per place.
 *
 * Output is RankedPlace-shaped (with sort_order applied via array order)
 * so SharedTripPage can render the new list using the same SharedPlaceCard.
 */

import type { SavedItinerary, SavedPlace } from '@/hooks/useItineraries';
import { CONCIERGE_VOICE, DESCRIPTION_SHAPE } from '@/prompts/system';
import { calculateDistanceMeters } from '@/utils/formatters';
import type { RankingSource } from '@/hooks/useLLM';
import { inferOpenStatus } from '@/utils/openingHoursInference';
import type { OpeningHours } from '@/services/googlePlaces';
import { callLLM, parseJsonArray, llmFingerprint } from '@/services/llmClient';
import { reportError } from '@/lib/reportError';
import { estimateTravelMins as travelMinsFromMeters, formatDistanceNote } from '@/utils/travelTime';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ReevaluateContext {
  /** Origin coordinates for travel-time estimation. Either:
   *   - 'current' → user's geolocation (passed in coords)
   *   - 'original' → POI centroid (computed from the shared trip's places) */
  locationMode: 'current' | 'original';
  /** Lat/lng of the origin used for haversine. Required. For 'original'
   *  mode, SharedTripPage computes the centroid of trip.places before
   *  calling reevaluate(). */
  originCoords: { lat: number; lng: number };
  /** Human-readable label of where the user is, for the LLM prompt. */
  locationLabel: string;
  /** 'walking' | 'transit' | 'taxi' — same vocabulary as InputScreen. */
  transport: string;
  /** '1-2-hrs' | 'half-day' | 'full-day' | '2-3-days' — same vocabulary. */
  timeAvailable: string;
  /** Output language. Defaults to 'en' if not set. */
  language: string;
}

export interface ReevaluatedPlace {
  /** SavedPlace id we re-ranked. */
  id: string;
  /** New 0-based position. */
  sort_order: number;
  /** New hook for the new context. */
  hook: string;
  /** Refreshed open-status summary at arrival time given new origin. */
  openStatusSummary: string;
  /** Computed travel time from new origin (haversine × roadFactor). */
  travelMins: number;
  /** Human distance label for UI: e.g. "12 min walk", "~6 min by transit". */
  distanceNote: string;
  /** Original SavedPlace passed through so the UI doesn't need to re-fetch. */
  source: SavedPlace;
  /** Who produced this ranking and hook: the AI, or the offline scorer. */
  rankingSource: RankingSource;
  /** Model id when rankingSource is 'ai'. */
  model?: string;
}

// ─── Constants ──────────────────────────────────────────────────────────────

/** Approximate budget in minutes per timeAvailable bucket. Matches
 *  TIME_MINUTES in prompts/ranking.ts:29-34. */
const TIME_BUDGET_MINS: Record<string, number> = {
  '1-2-hrs': 90,
  'half-day': 240,
  'full-day': 480,
  '2-3-days': 1440,
};

const TIME_LABELS: Record<string, string> = {
  '1-2-hrs': '1-2 hours',
  'half-day': 'half a day',
  'full-day': 'a full day',
  '2-3-days': '2-3 days',
};

/** Open-at-arrival gating only applies on short visits. Mirrors
 *  placeFilters.ts:21 — for `full-day` and `2-3-days` the traveller will
 *  pass each place at various times of day so a single-snapshot open
 *  check is misleading. Without this, Tune hooks have been saying
 *  places are "shut" / "closed at arrival" on long trips where the app
 *  would otherwise treat them as fine. */
const OPEN_GATED_SLOTS = new Set(['1-2-hrs', 'half-day']);

function shouldGateByOpenStatus(timeAvailable: string): boolean {
  return OPEN_GATED_SLOTS.has(timeAvailable);
}

const LANGUAGE_NAMES: Record<string, string> = {
  en: 'English',
  fr: 'French',
  es: 'Spanish',
  de: 'German',
};

// ─── Haversine + travel time ────────────────────────────────────────────────

function haversineMetres(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  return calculateDistanceMeters(a.lat, a.lng, b.lat, b.lng);
}

function estimateTravelMins(
  origin: { lat: number; lng: number },
  dest: { lat: number; lng: number },
  transport: string,
): number {
  return travelMinsFromMeters(haversineMetres(origin, dest), transport);
}

/** Build a faux OpeningHours-shaped object from SavedPlace.opening_hours
 *  so inferOpenStatus can reason about it. SavedPlace stores only
 *  openNow + weekdayDescriptions (see useItineraries.ts:48-52), no
 *  periods array, so inferOpenStatus falls back to typical-hours
 *  inference for arrival-time gating. That's fine for v1. */
function toOpeningHoursFaux(
  saved: SavedPlace['opening_hours'],
): OpeningHours | undefined {
  if (!saved) return undefined;
  return { openNow: saved.openNow };
}

// ─── Centroid helper for "original" location mode ───────────────────────────

export function centroidOf(places: SavedPlace[]): { lat: number; lng: number } | null {
  const withCoords = places.filter(
    (p): p is SavedPlace & { location_lat: number; location_lng: number } =>
      p.location_lat != null && p.location_lng != null,
  );
  if (withCoords.length === 0) return null;
  const sumLat = withCoords.reduce((s, p) => s + p.location_lat, 0);
  const sumLng = withCoords.reduce((s, p) => s + p.location_lng, 0);
  return {
    lat: sumLat / withCoords.length,
    lng: sumLng / withCoords.length,
  };
}

// ─── LLM prompt builder ─────────────────────────────────────────────────────

interface LLMPlaceContext {
  idx: number;
  name: string;
  rating: number | null;
  reviews: number;
  types: string[];
  category: string | null;
  openStatus: string;
  travelMins: number;
  /** Pre-formatted human phrase, e.g. "13 min walk", "~6 min by transit",
   *  "~4 min drive". Passed in so the LLM uses the same wording the rest of
   *  the app uses — previously hooks said generic "Travel Time: X min"
   *  without naming the transport. */
  distanceNote: string;
  fitsBudget: boolean;
}
// Note: do NOT add `hook` to LLMPlaceContext. Feeding the prior hook to the
// model causes it to light-edit instead of regenerating — most visibly, the
// prior travel-time number survives a transport switch (e.g. an 8-min taxi
// hook becomes "8 min by walking" instead of being re-derived from the new
// travelMins). Strip it; the prompt's "Reference travelMins, openStatus,
// or types specifically" instruction forces fresh generation.

function buildReevaluatePrompt(
  places: SavedPlace[],
  travelMins: number[],
  context: ReevaluateContext,
): { system: string; user: string } {
  const currentHour = new Date().getHours();
  const language = LANGUAGE_NAMES[context.language] || 'English';
  const timeLabel = TIME_LABELS[context.timeAvailable] || context.timeAvailable;
  const budgetMins = TIME_BUDGET_MINS[context.timeAvailable] || 240;
  const gateByOpen = shouldGateByOpenStatus(context.timeAvailable);

  const placeContext: LLMPlaceContext[] = places.map((p, idx) => {
    const t = travelMins[idx];
    let openStatus = 'hours unknown';
    if (gateByOpen) {
      const arrival = inferOpenStatus(
        toOpeningHoursFaux(p.opening_hours),
        p.types,
        currentHour,
        t,
      );
      openStatus = arrival.likelyOpen
        ? arrival.confidence === 'known'
          ? 'open at arrival (confirmed)'
          : 'likely open at arrival'
        : arrival.confidence === 'known'
          ? 'closed at arrival (confirmed)'
          : 'likely closed at arrival';
    }
    return {
      idx,
      name: p.name,
      rating: p.rating,
      reviews: p.user_ratings_total,
      types: p.types.slice(0, 5),
      category: p.category,
      openStatus,
      travelMins: t,
      distanceNote: formatDistanceNote(t, context.transport, context.language),
      // Round-trip travel + ~30min visit needs to fit budget
      fitsBudget: t * 2 + 30 <= budgetMins,
    };
  });

  const system = `You are TripBoom's concierge re-ranking an existing trip for a traveller in a new situation.

${CONCIERGE_VOICE}

You are given a saved trip and the traveller's context (where they are now, how they're getting around, how long they have). Your job is to re-rank the trip's places for them and write a fresh description for each, telling them why it fits their situation now — or doesn't.

${DESCRIPTION_SHAPE}

Each place carries a \`distanceNote\` with its travel time and mode, already in the output language. Quote it verbatim in the hook; do not restate the travel time in other words.${gateByOpen ? '' : '\n\nOpen-at-arrival status is intentionally not part of the input for this time window — the traveller has the full day (or longer) so they\'ll pass each place at various hours. Do not refer to places as "closed" or "shut" in the hook; focus on travelMins, rating, types, and fit-to-vibes instead.'}`;

  const ranking = gateByOpen
    ? `RANKING:
1. Open status at arrival wins — closed places rank last.
2. Places that don't fit the time budget (fitsBudget=false) rank lower.
3. Within those, prefer higher-rated and lower-travelMins places.
4. Keep all ${places.length} places — re-order, don't drop.`
    : `RANKING:
1. Places that don't fit the time budget (fitsBudget=false) rank lower.
2. Within those, prefer higher-rated and lower-travelMins places.
3. Keep all ${places.length} places — re-order, don't drop.
4. Do NOT use open-at-arrival status — the traveller has a full day or longer.`;

  const hookGuidance = gateByOpen
    ? 'hook: the description, addressed to the traveller as "you" and shaped as set out in DESCRIPTION — LENGTH AND SHAPE. It MUST include the place\'s `distanceNote` verbatim so they know the transport mode and time. Reference openStatus, rating or types specifically. Never make a claim the data does not support.'
    : 'hook: the description, addressed to the traveller as "you" and shaped as set out in DESCRIPTION — LENGTH AND SHAPE. It MUST include the place\'s `distanceNote` verbatim so they know the transport mode and time. Reference rating, types or fit-to-vibes specifically. Do NOT mention opening hours / closed / shut — the time window is long enough that opening status is not gating. Never make a claim the data does not support.';

  const user = `NEW TRAVELLER CONTEXT:
- Location: ${context.locationLabel}
- Getting around by: ${context.transport}
- Time available: ${timeLabel} (~${budgetMins} mins total)
- Current hour: ${currentHour}:00

PLACES (saved trip, ${places.length} total):
${JSON.stringify(placeContext)}

TASK: Re-rank these ${places.length} places for THIS traveller's new context. Output in ${language}.

For each place return:
- idx: numeric index from input (must match exactly)
- ${hookGuidance}

${ranking}

Return a JSON object with one key, "results", holding ${places.length} entries. No markdown, no explanation.`;

  return { system, user };
}

// ─── Engine ─────────────────────────────────────────────────────────────────

interface LLMReevalItem {
  idx: number | string;
  hook?: string;
}

/** Offline fallback: rank by (open at arrival on gated slots, fits budget,
 *  rating, -travelMins). For full-day / 2-3-days the open-status check is
 *  bypassed to match the app's behaviour in placeFilters.ts:21. */
function offlineFallback(
  places: SavedPlace[],
  travelMins: number[],
  context: ReevaluateContext,
): ReevaluatedPlace[] {
  const currentHour = new Date().getHours();
  const budgetMins = TIME_BUDGET_MINS[context.timeAvailable] || 240;
  const gateByOpen = shouldGateByOpenStatus(context.timeAvailable);

  const scored = places.map((p, idx) => {
    const t = travelMins[idx];
    const status = gateByOpen
      ? inferOpenStatus(
          toOpeningHoursFaux(p.opening_hours),
          p.types,
          currentHour,
          t,
        )
      : null;
    const fits = t * 2 + 30 <= budgetMins;
    const score =
      (gateByOpen && status ? (status.likelyOpen ? 4 : -2) : 0) +
      (fits ? 2 : -2) +
      (p.rating ?? 3) -
      Math.min(t / 30, 2);
    return { p, idx, t, status, fits, score };
  });

  scored.sort((a, b) => b.score - a.score);

  return scored.map((s, newIdx) => {
    let summary = 'hours not gating';
    if (gateByOpen && s.status) {
      summary = s.status.likelyOpen
        ? s.status.confidence === 'known'
          ? 'open at arrival'
          : 'likely open at arrival'
        : s.status.confidence === 'known'
          ? 'closed at arrival'
          : 'likely closed at arrival';
    }
    // Facts only: the saved hook, or nothing. Travel time is its own field.
    return {
      id: s.p.id,
      sort_order: newIdx,
      hook: s.p.hook || '',
      openStatusSummary: summary,
      travelMins: s.t,
      distanceNote: formatDistanceNote(s.t, context.transport, context.language),
      source: s.p,
      rankingSource: 'offline',
    };
  });
}

/** Places with no coordinates cannot be ranked by distance. They keep their
 *  saved hook and go to the end instead of being dropped (#87 §7.6). */
function appendUnlocated(ranked: ReevaluatedPlace[], unlocated: SavedPlace[], rankingSource: RankingSource, model?: string): ReevaluatedPlace[] {
  return ranked.concat(unlocated.map((p, i) => ({
    id: p.id,
    sort_order: ranked.length + i,
    hook: p.hook || '',
    openStatusSummary: 'hours unknown',
    travelMins: 0,
    distanceNote: '',
    source: p,
    rankingSource,
    ...(model && { model }),
  })));
}

/** Reevaluate a saved trip for a new traveller context.
 *  Returns ReevaluatedPlace[] with new sort_order + hook + open-status. */
export async function reevaluateTrip(
  trip: SavedItinerary,
  context: ReevaluateContext,
): Promise<ReevaluatedPlace[]> {
  const placesWithCoords = trip.places.filter(
    (p) => p.location_lat != null && p.location_lng != null,
  );
  const unlocated = trip.places.filter(
    (p) => p.location_lat == null || p.location_lng == null,
  );
  if (placesWithCoords.length === 0) return appendUnlocated([], trip.places, 'offline');

  const travelMins = placesWithCoords.map((p) =>
    estimateTravelMins(
      context.originCoords,
      { lat: p.location_lat as number, lng: p.location_lng as number },
      context.transport,
    ),
  );

  const substitute = (err: unknown): ReevaluatedPlace[] => {
    console.warn('Reevaluate LLM failed, using offline ranking:', err);
    reportError('llm.reevaluate', err, {
      candidates: placesWithCoords.length, timeAvailable: context.timeAvailable, transport: context.transport,
      language: context.language, anonymous: !!trip.share_slug,
    }, { level: 'warning', fingerprint: llmFingerprint('llm.reevaluate', err) });
    return appendUnlocated(offlineFallback(placesWithCoords, travelMins, context), unlocated, 'offline');
  };

  let llmResults: LLMReevalItem[] = [];
  let model = '';
  try {
    const { system, user } = buildReevaluatePrompt(placesWithCoords, travelMins, context);
    const result = await callLLM({ prompt: user, systemPrompt: system, maxTokens: 8000, schema: 'reevaluate' });
    llmResults = parseJsonArray(result.text) as LLMReevalItem[];
    model = result.model;
  } catch (err) {
    return substitute(err);
  }

  if (llmResults.length === 0) return substitute(new Error(`${model} returned no places`));

  // Map LLM results back to ReevaluatedPlace, preserving LLM-supplied
  // ranking via the response array order.
  const currentHour = new Date().getHours();
  const gateByOpen = shouldGateByOpenStatus(context.timeAvailable);
  const seen = new Set<number>();
  const ranked: ReevaluatedPlace[] = [];

  llmResults.forEach((item, newIdx) => {
    const idxNum =
      typeof item.idx === 'number' ? item.idx : parseInt(String(item.idx), 10);
    if (isNaN(idxNum) || idxNum < 0 || idxNum >= placesWithCoords.length) return;
    if (seen.has(idxNum)) return;
    seen.add(idxNum);

    const p = placesWithCoords[idxNum];
    const t = travelMins[idxNum];
    let summary = 'hours not gating';
    if (gateByOpen) {
      const status = inferOpenStatus(
        toOpeningHoursFaux(p.opening_hours),
        p.types,
        currentHour,
        t,
      );
      summary = status.likelyOpen
        ? status.confidence === 'known'
          ? 'open at arrival'
          : 'likely open at arrival'
        : status.confidence === 'known'
          ? 'closed at arrival'
          : 'likely closed at arrival';
    }

    ranked.push({
      id: p.id,
      sort_order: newIdx,
      hook: item.hook || p.hook || '',
      openStatusSummary: summary,
      travelMins: t,
      distanceNote: formatDistanceNote(t, context.transport, context.language),
      source: p,
      rankingSource: 'ai',
      model,
    });
  });

  // Append any places the LLM dropped (defensive — instruction says don't drop)
  // so the user still sees them at the bottom.
  placesWithCoords.forEach((p, idx) => {
    if (seen.has(idx)) return;
    const t = travelMins[idx];
    let summary = 'hours not gating';
    if (gateByOpen) {
      const status = inferOpenStatus(
        toOpeningHoursFaux(p.opening_hours),
        p.types,
        currentHour,
        t,
      );
      summary = status.likelyOpen ? 'likely open at arrival' : 'likely closed at arrival';
    }
    ranked.push({
      id: p.id,
      sort_order: ranked.length,
      hook: p.hook || '',
      openStatusSummary: summary,
      travelMins: t,
      distanceNote: formatDistanceNote(t, context.transport, context.language),
      source: p,
      rankingSource: 'ai',
      model,
    });
  });

  return appendUnlocated(ranked, unlocated, 'ai', model);
}
